import React from 'react';
import errorReporter from '@app/errorReporter';
import { PDFDocumentProxy, PDFPageProxy } from 'pdfjs-dist';
import message from '../Message';
import { PDFViewProps, FileType } from './types';
import PDFPage from './PDFPage';

const PDFView = <T extends FileType>({
  file,
  width,
  onLoad,
  firstPageOnly,
  render,
}: PDFViewProps<T>) => {
  const loading = React.useRef(false);
  const pdf = React.useRef<PDFDocumentProxy>(null);
  const [pages, setPages] = React.useState<PDFPageProxy[]>(null);
  const [pagesDrawn, setPagesDrawn] = React.useState<boolean[]>([]);

  const load = async () => {
    if (loading.current) return;

    try {
      // @ts-ignore
      const pdfjsLib = await import('pdfjs-dist/build/pdf');
      // @ts-ignore
      const pdfjsWorker = await import('pdfjs-dist/build/pdf.worker.entry');
      pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

      loading.current = true;
      if (pdf.current) pdf.current.destroy();
      pdf.current = (await pdfjsLib.getDocument({
        url: file.url,
        isEvalSupported: false,
      }).promise) as PDFDocumentProxy;
      const pageCount = firstPageOnly ? 1 : pdf.current.numPages;

      const promises: Promise<PDFPageProxy>[] = [];

      for (let i = 1; i <= pageCount; i++) {
        promises.push(
          new Promise((resolve, reject) => {
            pdf.current.getPage(i).then(resolve, reject);
          }),
        );
      }

      const newPages = await Promise.all(promises);

      setPagesDrawn(newPages.map(() => false));
      setPages(newPages);
    } catch (err) {
      errorReporter.error(err);
      message.error('Invalid file');
      onLoad();
    } finally {
      loading.current = false;
    }
  };

  const onPageDrawn = (i: number) => () => {
      setPagesDrawn(prev =>
        prev.map((bool, j) => {
          if (i === j) return true;
          return bool;
        }),
      );
    };

  React.useEffect(() => {
    load();
  }, [file.url]);

  React.useEffect(() => {
    if (pagesDrawn.length && pagesDrawn.length === pages?.length) {
      if (pagesDrawn.every(Boolean)) {
        onLoad();
      }
    }
  }, [pages, pagesDrawn]);

  return (
    <>
      {pages?.map((page, i) => (
        <PDFPage
          page={page}
          file={file}
          key={i}
          render={render}
          width={width}
          onPageDrawn={onPageDrawn(i)}
        />
      ))}
    </>
  );
};

export default PDFView;
